import * as React from "react";
import type { RichTextEditorProps } from "./RichTextEditor";

/**
 * RichTextEditorLazy — Suspense-wrapped lazy load of the Tiptap-backed
 * ``RichTextEditor``.
 *
 * Why: ``@tiptap/react`` + ``@tiptap/starter-kit`` + ``@tiptap/extension-
 * mention`` + ``@tiptap/extension-placeholder`` + ``tippy.js`` together
 * weigh ~150 KB gzipped. Statically importing them from
 * ``composer-parts/index.ts`` pulls the entire editor into the route's
 * eager chunk on every page that imports ``@ando/ui`` — even pages that
 * never render a composer. Lazy-loading lets the workspace hubs (the
 * dominant navigation paths) skip Tiptap entirely.
 *
 * Skeleton: a 92px-tall pulsing placeholder that matches the resting
 * height of the live composer (`min-h-[92px]` on `EditorContent`) so the
 * chrome doesn't reflow when the chunk lands.
 *
 * Public surface is identical to ``RichTextEditor`` — the namespace alias
 * ``Composer.RichEditor`` keeps pointing here, callers see no breaking
 * change. The lazy import is internal and the WebView's HTTP cache hits
 * after the first activation.
 */
const RichTextEditorImpl = React.lazy(() =>
  import("./RichTextEditor").then((mod) => ({ default: mod.RichTextEditor })),
);

function RichTextEditorFallback(): React.JSX.Element {
  return (
    <div
      aria-hidden
      className="min-h-[92px] animate-pulse rounded-md bg-black/[0.04]"
    />
  );
}

export const RichTextEditorLazy: React.FC<RichTextEditorProps> = (props) => (
  <React.Suspense fallback={<RichTextEditorFallback />}>
    <RichTextEditorImpl {...props} />
  </React.Suspense>
);
RichTextEditorLazy.displayName = "RichTextEditorLazy";
